<template>
  <div class="dashboard">
    <!-- Mensagem se nenhum tipo selecionado -->
    <v-fade-transition>
      <v-alert v-if="selectedProjectType === null" type="info" variant="tonal" class="mb-4 border-opacity-50" border="start" elevation="1">
        <template v-slot:prepend>
          <v-icon size="large">mdi-information</v-icon>
        </template>
        {{ $t('dashboard.selectType') }}
      </v-alert>
    </v-fade-transition>

    <!-- Header do Dashboard: tipo + sottogruppi integrati -->
    <v-card class="mb-4 rounded-lg" elevation="2">
      <div class="d-flex pa-2" style="gap: 8px;">
        <div
          v-for="type in projectTypeSummary"
          :key="type.id"
          class="flex-grow-1"
          style="min-width: 0;"
        >
          <!-- Pulsante tipo principale -->
          <v-btn
            :color="selectedProjectType === type.id ? 'primary' : 'grey-lighten-4'"
            :variant="selectedProjectType === type.id ? 'elevated' : 'flat'"
            block
            size="large"
            class="text-none mb-0 project-type-btn"
            :class="{ 'active-type': selectedProjectType === type.id }"
            :style="selectedProjectType === type.id && groupSummary.length > 0 ? 'border-bottom-left-radius: 0; border-bottom-right-radius: 0;' : 'border-radius: 8px;'"
            @click="selectProjectType(type.id)"
          >
            <span class="mr-2 text-h5">{{ type.icon }}</span>
            <span class="font-weight-bold text-subtitle-1" :class="selectedProjectType === type.id ? 'text-white' : 'text-grey-darken-3'">{{ type.descr }}</span>
            <v-chip
              size="small"
              :color="selectedProjectType === type.id ? 'white' : 'grey-darken-1'"
              :text-color="selectedProjectType === type.id ? 'primary' : 'white'"
              class="ml-3 font-weight-bold"
              variant="elevated"
              elevation="1"
            >
              {{ type.count }}
            </v-chip>
          </v-btn>

          <!-- Sottogruppi (visibili solo se questo tipo è selezionato) -->
          <v-expand-transition>
            <div
              v-if="selectedProjectType === type.id && groupSummary.length > 0"
              class="d-flex bg-grey-lighten-4"
              style="gap: 1px; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; overflow: hidden; border: 1px solid rgb(var(--v-theme-primary)); border-top: none;"
            >
              <v-btn
                v-for="group in groupSummary"
                :key="group.key"
                :color="selectedGroup === group.key ? 'primary' : 'transparent'"
                :variant="selectedGroup === group.key ? 'flat' : 'text'"
                class="text-none flex-grow-1 rounded-0"
                size="small"
                height="36"
                @click="selectGroup(group.key)"
              >
                <span :class="selectedGroup === group.key ? 'text-white' : 'text-grey-darken-3'">{{ $t('dashboard.groups.' + group.key, group.key) }}</span>
                <v-chip
                  size="small"
                  :color="selectedGroup === group.key ? 'white' : 'grey-darken-1'"
                  :text-color="selectedGroup === group.key ? 'primary' : 'white'"
                  class="ml-2 font-weight-bold"
                  variant="elevated"
                  elevation="1"
                >
                  {{ group.count }}
                </v-chip>
                <v-badge v-if="group.warningCount > 0" :content="group.warningCount" color="orange-darken-1" inline class="ml-1"></v-badge>
                <v-badge v-if="group.overdueCount > 0" :content="group.overdueCount" color="red-darken-1" inline class="ml-1"></v-badge>
              </v-btn>
            </div>
          </v-expand-transition>
        </div>
      </div>
    </v-card>

    <!-- Toolbar de Status (filtrado pelo tipo e gruppo selezionato) -->
    <v-expand-transition>
      <div v-if="selectedProjectType !== null" class="mb-4">
        <div class="status-grid">
          <v-card
            v-for="status in visibleStatusSummary"
            :key="status.id"
            :color="selectedStatus === status.id ? status.color : 'white'"
            :class="['status-card', selectedStatus === status.id ? 'status-card--active' : '', status.count === 0 ? 'status-card--disabled' : '']"
            :elevation="selectedStatus === status.id ? 4 : 1"
            @click="status.count > 0 ? selectStatus(status.id) : null"
            v-ripple="status.count > 0"
            border
            density="compact"
          >
            <div class="d-flex align-center pa-2 h-100">
              <div class="d-flex align-center mr-3">
                <span class="text-h6 mr-2">{{ status.icon }}</span>
                <span class="text-h6 font-weight-bold" :class="selectedStatus === status.id ? 'text-white' : 'text-grey-darken-4'">
                  {{ status.count }}
                </span>
              </div>
              <div class="d-flex align-center flex-grow-1 justify-space-between" style="min-width: 0;">
                <span class="text-body-2 font-weight-medium text-truncate" :class="selectedStatus === status.id ? 'text-white' : 'text-grey-darken-3'" :title="status.descr">
                  {{ status.descr }}
                </span>
                <div class="d-flex ml-2 flex-shrink-0" style="gap: 2px;" v-if="status.warningCount > 0 || status.overdueCount > 0">
                  <v-badge v-if="status.warningCount > 0" :content="status.warningCount" color="orange-darken-1" inline></v-badge>
                  <v-badge v-if="status.overdueCount > 0" :content="status.overdueCount" color="red-darken-1" inline></v-badge>
                </div>
              </div>
            </div>
          </v-card>
        </div>
      </div>
    </v-expand-transition>

    <!-- Tabela de Projetos -->
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-lg" elevation="2">
          <v-card-title class="bg-grey-lighten-4 py-3 border-bottom">
            <v-icon left color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
            <span class="text-subtitle-1 font-weight-bold text-grey-darken-3">{{ $t('projects.title') }}</span>
          </v-card-title>

          <v-card-text class="pa-0">
            <ProjectsTable ref="projectsTable" @project-saved="onProjectSaved" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Quick Actions FAB -->
    <v-btn icon="mdi-plus" color="primary" size="large" elevation="8" class="floating-fab"
      @click="openCreateProject"></v-btn>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import ProjectsTable from '../components/ProjectsTable.vue'

const projectStore = useProjectStore()
const projectsTable = ref()
const selectedProjectType = ref(null)
const selectedStatus = ref(null)
const selectedGroup = ref(null)
const isRefreshing = ref(false)

// Computed per riepilogo dei tipi di progetto (Importação/Exportação)
const projectTypeSummary = computed(() => {
  const typeCounts = {}
  
  // Inizializza contatori per tutti i tipi
  projectStore.projectTypes.forEach(type => {
    typeCounts[type.id] = 0
  })
  
  // Conta progetti per tipo - usa allProjects per avere tutti i progetti
  const projectsToCount = projectStore.allProjects.length > 0 ? projectStore.allProjects : projectStore.projects
  projectsToCount.forEach(project => {
    const typeId = parseInt(project.projectType)
    if (typeCounts.hasOwnProperty(typeId)) {
      typeCounts[typeId]++
    }
  })
  
  // Ritorna array con info dei tipi e contatori
  return projectStore.projectTypes.map(type => ({
    ...type,
    count: typeCounts[type.id] || 0,
    icon: type.id === 0 ? '📥' : '📤' // Importação / Exportação
  }))
})

// Label del tipo di progetto selezionato
const selectedProjectTypeLabel = computed(() => {
  if (selectedProjectType.value === null) return ''
  const type = projectStore.projectTypes.find(t => t.id === selectedProjectType.value)
  return type?.descr || ''
})

// Computed para resumo dos status (filtrato per tipo selezionado)
const filteredStatusSummary = computed(() => {
  if (selectedProjectType.value === null) return []
  
  const statusCounts = {}
  const warningFollowUpCounts = {}
  const overdueFollowUpCounts = {}
  
  // Get today's date in local timezone as string
  const today = new Date()
  const todayStr = today.getFullYear() + '-' + 
                   String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                   String(today.getDate()).padStart(2, '0')
  
  // Ottieni la lista di status per il tipo selezionato
  const statusListForType = projectStore.getStatusListForType(selectedProjectType.value)
  
  // Inizializza contatori per tutti gli status del tipo selezionato
  statusListForType.forEach(status => {
    statusCounts[status.id] = 0
    warningFollowUpCounts[status.id] = 0
    overdueFollowUpCounts[status.id] = 0
  })
  
  // Conta progetti per status, filtrando per tipo selezionato - usa allProjects
  const projectsToCount = projectStore.allProjects.length > 0 ? projectStore.allProjects : projectStore.projects
  projectsToCount.forEach(project => {
    const projectTypeId = parseInt(project.projectType)
    const statusId = parseInt(project.activeStatus)
    
    // Conta solo se il tipo corrisponde
    if (projectTypeId === selectedProjectType.value) {
      if (statusCounts.hasOwnProperty(statusId)) {
        statusCounts[statusId]++
        
        // Verifica se il follow-up è oggi (warning) o scaduto (overdue)
        if (project.reminderDate) {
          const reminderStr = project.reminderDate.substring(0, 10)
          
          if (reminderStr === todayStr) {
            warningFollowUpCounts[statusId]++
          } else if (reminderStr < todayStr) {
            overdueFollowUpCounts[statusId]++
          }
        }
      }
    }
  })
  
  // Ritorna array con info degli status e contatori
  return statusListForType.map(status => ({
    ...status,
    count: statusCounts[status.id] || 0,
    warningCount: warningFollowUpCounts[status.id] || 0,
    overdueCount: overdueFollowUpCounts[status.id] || 0
  }))
})

// Riepilogo per gruppo: contatori aggregati degli status per gruppo
const groupSummary = computed(() => {
  if (selectedProjectType.value === null) return []

  // Ottieni le chiavi dei gruppi nell'ordine in cui appaiono nella statusList
  const groupOrder = []
  const seenGroups = new Set()
  const statusListForType = projectStore.getStatusListForType(selectedProjectType.value)
  statusListForType.forEach(s => {
    const g = s.group
    if (g && !seenGroups.has(g)) {
      seenGroups.add(g)
      groupOrder.push(g)
    }
  })

  return groupOrder.map(groupKey => {
    const statusesInGroup = filteredStatusSummary.value.filter(s => s.group === groupKey)
    return {
      key: groupKey,
      count: statusesInGroup.reduce((sum, s) => sum + s.count, 0),
      warningCount: statusesInGroup.reduce((sum, s) => sum + s.warningCount, 0),
      overdueCount: statusesInGroup.reduce((sum, s) => sum + s.overdueCount, 0)
    }
  })
})

// Status visibili: filtrati per gruppo selezionato (se applicabile)
const visibleStatusSummary = computed(() => {
  if (selectedGroup.value === null) return filteredStatusSummary.value
  return filteredStatusSummary.value.filter(s => s.group === selectedGroup.value)
})

// Watch to auto-deselect status if count becomes 0
watch(filteredStatusSummary, (newSummary) => {
  if (selectedStatus.value !== null) {
    const currentStatus = newSummary.find(s => s.id === selectedStatus.value)
    if (currentStatus && currentStatus.count === 0) {
      selectedStatus.value = null
      projectStore.setStatusFilter(null)
    }
  }
})

// Methods
const selectProjectType = (typeId) => {
  const normalizedTypeId = typeId !== undefined ? typeId : null
  selectedProjectType.value = normalizedTypeId
  selectedStatus.value = null
  selectedGroup.value = null
  projectStore.setProjectTypeFilter(normalizedTypeId)
  projectStore.setStatusFilter(null)
  projectStore.setGroupFilter(null)
}

const selectGroup = (groupKey) => {
  if (selectedGroup.value === groupKey) {
    // Toggle: deseleziona il gruppo
    selectedGroup.value = null
    projectStore.setGroupFilter(null)
  } else {
    selectedGroup.value = groupKey
    projectStore.setGroupFilter(groupKey)
    // Deseleziona lo status specifico se appartiene a un altro gruppo
    if (selectedStatus.value !== null) {
      const statusListForType = projectStore.getStatusListForType(selectedProjectType.value)
      const currentStatusConfig = statusListForType.find(s => s.id === selectedStatus.value)
      if (currentStatusConfig?.group !== groupKey) {
        selectedStatus.value = null
        projectStore.setStatusFilter(null)
      }
    }
  }
}

const selectStatus = (statusId) => {
  // Toggle: se clicco sullo stesso status, lo deseleziono
  if (selectedStatus.value === statusId) {
    selectedStatus.value = null
    projectStore.setStatusFilter(null)
  } else {
    selectedStatus.value = statusId
    projectStore.setStatusFilter(statusId)
  }
  // Non ricarica i dati, usa solo il filtro locale
}

const openCreateProject = () => {
  if (projectsTable.value) {
    projectsTable.value.openCreateDialog()
  }
}

const onProjectSaved = () => {
  // Atualizar dados quando um projeto for salvo (criado ou editado)
  // Mantém o filtro de status atual
  projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter)
}

// Função de refresh manual
const manualRefresh = async () => {
  isRefreshing.value = true
  try {
    console.log('Manual refresh triggered...')
    
    // Para o timer automático
    stopAutoRefresh()
    
    // Carrega os dados atualizados
    await projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter)
    
    // Reinicia o timer automático
    startAutoRefresh()
  } catch (error) {
    console.error('Error during manual refresh:', error)
  } finally {
    isRefreshing.value = false
  }
}

// Auto-refresh a cada 2 minutos
let refreshInterval = null

const startAutoRefresh = () => {
  // Atualiza os dados a cada 2 minutos (120000 ms)
  refreshInterval = setInterval(async () => {
    console.log('Auto-refreshing dashboard data silently...')
    // Usa refresh silenzioso per non disturbare l'utente
    await projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter, true)
  }, 120000) // 2 minutos
}

const stopAutoRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}

// Watch per sincronizzare i card quando cambiano i filtri dalla tabella
watch(() => projectStore.statusFilter, (newStatusFilter) => {
  // Aggiorna il selectedStatus della dashboard quando cambia il filtro nello store
  selectedStatus.value = newStatusFilter
})

watch(() => projectStore.projectTypeFilter, (newTypeFilter) => {
  // Aggiorna il selectedProjectType della dashboard quando cambia il filtro nello store
  selectedProjectType.value = newTypeFilter
})

// Lifecycle
onMounted(async () => {
  // Sincronizza lo stato locale con lo store
  selectedProjectType.value = projectStore.projectTypeFilter
  selectedStatus.value = projectStore.statusFilter

  // Carregar configurações e projetos iniciais
  if (!projectStore.statusList.length) {
    await projectStore.loadConfigurations()
  }
  
  if (!projectStore.projects.length) {
    await projectStore.loadProjects()
  }
  
  // Inicia o auto-refresh
  startAutoRefresh()
  
  // Ascolta eventi di refresh dalla toolbar
  window.addEventListener('dashboard-refresh', manualRefresh)
})

onUnmounted(() => {
  // Limpa o intervalo quando o componente é desmontado
  stopAutoRefresh()
  
  // Remove listener de eventos
  window.removeEventListener('dashboard-refresh', manualRefresh)
})
</script>

<style scoped>
.dashboard {
  padding: 16px;
  max-width: 100%;
  margin: 0 auto;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.project-type-btn {
  transition: all 0.3s ease;
}

.project-type-btn.active-type {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1) !important;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.status-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;
  height: 48px;
  border: 1px solid rgba(0,0,0,0.05);
}

.status-card:hover:not(.status-card--disabled) {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.1) !important;
}

.status-card--active {
  transform: translateY(-2px);
  border-color: transparent;
}

.status-card--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f5f5f5 !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.count-badge :deep(.v-badge__badge) {
  font-size: 1.1rem !important;
  min-width: 28px !important;
  height: 28px !important;
  padding: 0 8px !important;
  font-weight: bold !important;
}

.v-card {
  transition: transform 0.2s ease-in-out;
}

.cursor-pointer {
  cursor: pointer;
}

.text-h6 {
  font-weight: 600;
}

.floating-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}
</style>